import { formatDate } from '@telerik/kendo-intl';
import { Render } from 'Components/Display';
import { useEnums } from 'Context';
import React, { useMemo } from 'react';
import { useLineOfBusinessParam } from 'Utilities';
import { parseRawDateString } from 'Utilities/DateTimeUtc';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import Dropdown from 'v3/Components/Form/Dropdown';
import { FieldError } from 'v3/Components/Form/FormSection';
import { InputActive } from 'v3/Components/Form/InputActive';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { TitleActive } from 'v3/Components/Form/TitleActive';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import './AddEditReferralsRequest.AdditionalInformation.scss';
import './AddEditReferralsRequest.ClaimantInformation.scss';
import {
  createReferralRequestSchema,
  useReferralRequestForm,
} from './useReferralRequestForm';

type ClaimantInformationProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
};

export const ClaimantInformationActive: React.FC<ClaimantInformationProps> = ({
  form,
  fieldErrors,
}) => {
  const { lob } = useLineOfBusinessParam();
  const { gender, state } = useEnums();

  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();

  return (
    <div className="">
      <TitleActive>Claimant Identity</TitleActive>
      <div className="active-row">
        <div className="active-fields">
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <InputActive
              id="firstName"
              name="firstName"
              label="First Name"
              value={form.getFieldProps('firstName').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('firstName', e.target.value);
              }}
              required
              error={fieldErrors?.find((f) => f.fieldId === 'firstName')?.error}
            />
          </Render>
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <InputActive
              id="lastName"
              name="lastName"
              label="Last Name"
              value={form.getFieldProps('lastName').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('lastName', e.target.value);
              }}
              required
              error={fieldErrors?.find((f) => f.fieldId === 'lastName')?.error}
            />
          </Render>
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <Dropdown
              label="Gender"
              required
              value={form.getFieldProps('gender').value}
              onChange={(value) => form.setFieldValue('gender', value)}
              options={[]}
              enums={gender}
              error={fieldErrors?.find((f) => f.fieldId === 'gender')?.error}
            />
          </Render>
        </div>
      </div>
      <div className="active-row">
        <div className="active-fields">
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <DateTimePickerInput
              label={'Date of Birth'}
              value={form.getFieldProps('dateOfBirth').value}
              required
              onChange={(value) => form.setFieldValue('dateOfBirth', value)}
              error={
                fieldErrors?.find((f) => f.fieldId === 'dateOfBirth')?.error
              }
              dateOnly
            />
            <div className="empty-container"></div>
            <div className="empty-container"></div>
          </Render>
        </div>
      </div>
      <Render condition={isIME}>
        <TitleActive>Claimant Address</TitleActive>
      </Render>
      <Render condition={isIME}>
        <div className="active-row">
          <InputActive
            id="streetAddress"
            name="streetAddress"
            label="Street Address"
            value={form.getFieldProps('streetAddress').value ?? ''}
            onChange={(e: any) => {
              form.setFieldValue('streetAddress', e.target.value);
            }}
            required
            error={
              fieldErrors?.find((f) => f.fieldId === 'streetAddress')?.error
            }
            className="input-container street-address"
          />
        </div>
      </Render>
      <div className="active-row">
        <div className="active-fields">
          <Render condition={isIME}>
            <InputActive
              id="city"
              name="city"
              label="City"
              value={form.getFieldProps('city').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('city', e.target.value);
              }}
              required
              error={fieldErrors?.find((f) => f.fieldId === 'city')?.error}
            />
          </Render>
          <Render condition={isIME}>
            <Dropdown
              label="State"
              required
              value={form.getFieldProps('state').value}
              onChange={(value) => form.setFieldValue('state', value)}
              options={[]}
              enums={state}
              valueAsDisplayName
              error={fieldErrors?.find((f) => f.fieldId === 'state')?.error}
            />
          </Render>
          <Render condition={isIME}>
            <InputActive
              id="zipCode"
              name="zipCode"
              label="Zip Code"
              value={form.getFieldProps('zipCode').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('zipCode', e.target.value);
              }}
              required
              error={fieldErrors?.find((f) => f.fieldId === 'zipCode')?.error}
            />
          </Render>
        </div>
      </div>

      <Render condition={isIME}>
        <TitleActive>Claimant Contact Details</TitleActive>
      </Render>
      <div className="active-row">
        <div className="active-fields">
          <Render condition={isIME}>
            <InputActive
              id="phone"
              name="phone"
              label="Phone Number"
              value={form.getFieldProps('phone').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('phone', e.target.value);
              }}
              required={!isIME}
              error={fieldErrors?.find((f) => f.fieldId === 'phone')?.error}
            />
          </Render>
          <Render condition={isIME}>
            <InputActive
              id="email"
              name="email"
              label="Email"
              value={form.getFieldProps('email').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('email', e.target.value);
              }}
              required={!isIME}
              error={fieldErrors?.find((f) => f.fieldId === 'email')?.error}
            />
          </Render>
          <div className="empty-container"></div>
        </div>
      </div>
    </div>
  );
};

export const ClaimantInformationReadOnly: React.FC<
  ClaimantInformationProps
> = ({ form }) => {
  const { lob } = useLineOfBusinessParam();
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );

  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();

  return (
    <div className="read-only-content">
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleReadOnly>Claimant Identity</TitleReadOnly>
        <ReadOnlyField
          id="firstName"
          label="First Name"
          value={form.getFieldProps('firstName').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="lastName"
          label="Last Name"
          value={form.getFieldProps('lastName').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="gender"
          label="Gender"
          value={form.getFieldProps('gender.displayName').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="dateOfBirth"
          label="Date of Birth"
          value={
            form.getFieldProps('dateOfBirth').value
              ? formatDate(
                  parseRawDateString(form.getFieldProps('dateOfBirth').value) || new Date(),
                  'd'
                )
              : ''
          }
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render condition={isIME}>
        <TitleReadOnly>Claimant Address</TitleReadOnly>
        <ReadOnlyField
          id="streetAddress"
          label="Street Address"
          value={form.getFieldProps('streetAddress').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="city"
          label="City"
          value={form.getFieldProps('city').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="state"
          label="state"
          value={form.getFieldProps('state.displayName').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="zipCode"
          label="Zip Code"
          value={form.getFieldProps('zipCode').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <TitleReadOnly>Claimant Contact Details</TitleReadOnly>
        <ReadOnlyField
          id="phone"
          label="Phone Number"
          value={form.getFieldProps('phone').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="email"
          label="Email"
          value={form.getFieldProps('email').value ?? ''}
          schema={ReferralRequestSchema}
        />
      </Render>
    </div>
  );
};
